import { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { LogtoDataProvider } from '../../dataProvider';
import type { SettingsField, Template, TemplateSecretStatus } from '../../types';
import { groupFields, isRequired, isStoredSeparately, normalizeDomain } from './schema';
import { TemplateSecretInput } from './TemplateSecretInput';
import { apiErrorMessage } from './apiError';

interface Props {
  clientId: string;
  template: Template;
  features: Record<string, unknown>;
  onChange: (features: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

// Renders a template's extra settings from its settings schema, so a new
// template needs no dashboard changes. Non-secret values are edited in
// `features` (saved with the configuration); secrets and files are saved
// immediately through TemplateSecretInput.
export const TemplateSettingsForm = ({ clientId, template, features, onChange, errors = {}, disabled }: Props) => {
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const [secrets, setSecrets] = useState<Record<string, TemplateSecretStatus>>({});
  const [secretsError, setSecretsError] = useState<string | null>(null);
  const schema = template.settingsSchema;
  const hasSeparate = schema.fields.some(isStoredSeparately);

  useEffect(() => {
    if (!hasSeparate) return;
    dataProvider
      .getTemplateSecrets(clientId, template.key)
      .then((list) => setSecrets(Object.fromEntries(list.map((s) => [s.key, s]))))
      .catch((e) => setSecretsError(apiErrorMessage(e, 'Не удалось загрузить секреты шаблона')));
  }, [clientId, template.key, hasSeparate, dataProvider]);

  const set = (key: string, value: unknown) => onChange({ ...features, [key]: value });

  const renderField = (field: SettingsField) => {
    const required = isRequired(schema, field, features);
    const label = field.label || field.key;
    const error = errors[field.key];

    if (isStoredSeparately(field)) {
      return (
        <TemplateSecretInput
          key={field.key}
          clientId={clientId}
          templateKey={template.key}
          field={field}
          status={secrets[field.key]}
          required={required}
          disabled={disabled}
          onChanged={(status) =>
            setSecrets((prev) => {
              const next = { ...prev };
              if (status) next[field.key] = status;
              else delete next[field.key];
              return next;
            })
          }
        />
      );
    }

    switch (field.type) {
      case 'boolean':
        return (
          <Box key={field.key} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={features[field.key] === true || features[field.key] === 'true'}
                  onChange={(e) => set(field.key, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={label}
            />
            {(error || field.help) && (
              <FormHelperText error={!!error} sx={{ ml: 2 }}>
                {error || field.help}
              </FormHelperText>
            )}
          </Box>
        );
      case 'select':
        return (
          <FormControl key={field.key} fullWidth sx={{ mb: 2 }} required={required} error={!!error}>
            <InputLabel id={`select-${field.key}`}>{label}</InputLabel>
            <Select
              labelId={`select-${field.key}`}
              label={label}
              value={String(features[field.key] ?? field.default ?? '')}
              onChange={(e) => set(field.key, e.target.value)}
              disabled={disabled}
            >
              {(field.options || []).map((o) => (
                <MenuItem key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {(error || field.help) && <FormHelperText>{error || field.help}</FormHelperText>}
          </FormControl>
        );
      default:
        return (
          <TextField
            key={field.key}
            label={label}
            value={(features[field.key] as string) ?? ''}
            onChange={(e) => set(field.key, field.type === 'domain' ? normalizeDomain(e.target.value) : e.target.value)}
            fullWidth
            required={required}
            disabled={disabled}
            error={!!error}
            helperText={error || field.help}
            sx={{ mb: 2 }}
          />
        );
    }
  };

  if (schema.fields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        У шаблона нет дополнительных настроек.
      </Typography>
    );
  }

  return (
    <Box>
      {secretsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {secretsError}
        </Alert>
      )}
      {groupFields(schema.fields).map((group) => (
        <Box key={group.name || 'default'} sx={{ mb: 2 }}>
          {group.name && (
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              {group.name}
            </Typography>
          )}
          {group.fields.map(renderField)}
        </Box>
      ))}
    </Box>
  );
};
